import React, { useState } from 'react';
import { Icon, ICONS } from 'components/Icons';
import ToolTip from 'components/elements/atoms/ToolTip/ToolTip';
import { toast } from 'components/elements/molecules/Toast/Toast';
import * as syllabusApi from 'services/syllabusApi';
import {
  formatSyllabusDate,
  parseSyllabusDate,
} from 'routes/Portfolio/Component/CoursesTab/Topic/components/utils/dateHelper';
import DescriptionModal from './DescriptionModal';

const DESCRIPTION_PREVIEW_LIMIT = 1000;

export default function SyllabusScheduleTable({
  rows,
  onChange,
  courseId,
  deliveryDay = 'Sunday',
}) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [error, setError] = useState('');
  const [generatingReadings, setGeneratingReadings] = useState({});
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [descriptionModalIndex, setDescriptionModalIndex] = useState(null);

  const getDescriptionPreview = (description = '') => {
    if (description.length <= DESCRIPTION_PREVIEW_LIMIT) {
      return description;
    }

    return description.slice(0, DESCRIPTION_PREVIEW_LIMIT);
  };

  const openDescriptionModal = (index) => {
    setDescriptionModalIndex(index);
    setDescriptionModalOpen(true);
  };

  const handleDescriptionSave = async (saveData) => {
    const index = descriptionModalIndex;
    const row = rows[index];
    const topic = (saveData.topic || row.topic || row.name || '').trim();
    const date = saveData.date || row.date;
    const description = (saveData.description || '').trim();

    const updatedRow = {
      date,
      name: topic,
      description,
    };

    try {
      if (row._id) {
        await syllabusApi.updateSyllabusRow(courseId, row._id, updatedRow);
      } else {
        const result = await syllabusApi.addSyllabusRow(courseId, updatedRow);
        updatedRow._id = result?._id || result?.rowId;
      }
      const updated = rows.map((r, i) =>
        i === index
          ? {
              ...r,
              ...updatedRow,
              topic,
            }
          : r,
      );
      onChange(updated);
      setError('');
    } catch (error) {
      console.error('Error saving description:', error);
      setError('Failed to save description. Please try again.');
      throw error;
    }
  };

  const startEdit = (index) => {
    // Validation: Can't edit if another row is being edited
    if (editingIndex !== null && editingIndex !== index) {
      setError('Please save or cancel the current row before editing another.');
      return;
    }

    setError('');
    setEditingIndex(index);
    const row = rows[index];
    const normalizedDate = parseSyllabusDate(row.date);
    const normalized = normalizedDate
      ? normalizedDate.format('YYYY-MM-DD')
      : '';
    setEditValues({
      date: normalized,
      topic: row.topic || row.name || '',
      description: row.description || '',
    });
  };

  const saveEdit = async (index) => {
    // Validation: Both date and topic are required
    if (
      !editValues.date ||
      !editValues.topic ||
      editValues.topic.trim() === ''
    ) {
      setError('Both date and lecture topic are required.');
      return;
    }

    setError('');
    const row = rows[index];
    const updatedRow = {
      date: editValues.date,
      name: editValues.topic.trim(),
      description: (editValues.description || '').trim(),
    };

    try {
      if (row._id) {
        await syllabusApi.updateSyllabusRow(courseId, row._id, updatedRow);
      } else {
        const result = await syllabusApi.addSyllabusRow(courseId, updatedRow);
        updatedRow._id = result._id || result.rowId;
      }
      const updated = rows.map((r, i) =>
        i === index
          ? { ...r, ...updatedRow, topic: editValues.topic.trim() }
          : r,
      );
      onChange(updated);
      setEditingIndex(null);
    } catch (error) {
      console.error('Error saving row:', error);
      setError('Failed to save row. Please try again.');
    }
  };

  const cancelEdit = () => {
    // If it's a new empty row, remove it
    if (
      editingIndex !== null &&
      !rows[editingIndex]._id &&
      !rows[editingIndex].date &&
      !rows[editingIndex].topic &&
      !rows[editingIndex].description
    ) {
      onChange(rows.filter((_, i) => i !== editingIndex));
    }
    setEditingIndex(null);
    setError('');
  };

  const deleteRow = async (index) => {
    // Validation: Can't delete if a row is being edited
    if (editingIndex !== null) {
      setError('Please save or cancel the current row before deleting.');
      return;
    }

    const row = rows[index];
    try {
      if (row._id) {
        await syllabusApi.deleteSyllabusRow(courseId, row._id);
      }
      onChange(rows.filter((_, i) => i !== index));
      setError('');
    } catch (error) {
      console.error('Error deleting row:', error);
      setError('Failed to delete row. Please try again.');
    }
  };

  const addRow = () => {
    // Validation: Can't add new row if another row is being edited
    if (editingIndex !== null) {
      setError(
        'Please save or cancel the current row before adding a new one.',
      );
      return;
    }

    setError('');
    const newRows = [
      ...rows,
      { date: '', weekStartDate: '', topic: '', description: '' },
    ];
    onChange(newRows);
    setEditingIndex(newRows.length - 1);
    setEditValues({ date: '', topic: '', description: '' });
  };

  const formatDate = (date) => {
    return formatSyllabusDate(date, 'MM/DD/YYYY');
  };

  const formatReadingsScheduleDate = (scheduleDate) => {
    return formatSyllabusDate(scheduleDate, 'dddd, MMM Do');
  };

  const getReadingsScheduleDate = (row) => {
    if (!row?.weekStartDate) {
      return '';
    }

    return formatReadingsScheduleDate(row.weekStartDate);
  };

  const getScheduleTooltipText = () =>
    `On ${deliveryDay}s, we generate readings for all lectures for the upcoming week. You can change this schedule in settings.`;

  const handleGenerateReadings = async (row, index) => {
    setGeneratingReadings((prev) => ({ ...prev, [index]: true }));
    try {
      // TODO: API call will be added here
      await syllabusApi.generateReadings(courseId, row._id);

      console.log(`Generating readings for: ${row.name || row.topic}`);
      toast(
        `Readings for "${row.name || row.topic}" are being generated. You'll receive an email once they're ready.`,
        {
          type: 'success',
        },
      );
    } catch (error) {
      console.error('Error generating readings:', error);
      toast('Failed to generate readings. Please try again.', {
        type: 'error',
      });
    } finally {
      setGeneratingReadings((prev) => ({ ...prev, [index]: false }));
    }
  };

  return (
    <>
      <DescriptionModal
        show={descriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        value={
          descriptionModalIndex !== null
            ? rows[descriptionModalIndex]?.description
            : ''
        }
        lectureTopic={
          descriptionModalIndex !== null
            ? rows[descriptionModalIndex]?.topic ||
              rows[descriptionModalIndex]?.name ||
              ''
            : ''
        }
        lectureDate={
          descriptionModalIndex !== null
            ? rows[descriptionModalIndex]?.date || ''
            : ''
        }
        readingsScheduleDate={
          descriptionModalIndex !== null
            ? rows[descriptionModalIndex]?.weekStartDate || ''
            : ''
        }
        onSave={handleDescriptionSave}
        title="Edit Description"
      />
      <div className="syllabus-table-wrapper">
        {error && <div className="syllabus-error mb-2">{error}</div>}
        <table className="editable-table">
          <thead>
            <tr>
              <th>Lecture Date</th>
              <th>Lecture topic</th>
              <th>Description</th>
              <th>
                <ToolTip
                  toolTipText={getScheduleTooltipText()}
                  toolTipPosition="top"
                >
                  Readings schedule
                </ToolTip>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={index}
                className={editingIndex === index ? 'is-editing-row' : ''}
              >
                {editingIndex === index ? (
                  <>
                    <td className="syllabus-date-cell">
                      <input
                        type="date"
                        className="syllabus-input"
                        value={editValues.date}
                        onChange={(e) =>
                          setEditValues((v) => ({ ...v, date: e.target.value }))
                        }
                      />
                    </td>
                    <td className="syllabus-topic-cell">
                      <input
                        type="text"
                        className="syllabus-input"
                        placeholder="Enter lecture topic"
                        value={editValues.topic}
                        onChange={(e) =>
                          setEditValues((v) => ({
                            ...v,
                            topic: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit(index);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                      />
                    </td>
                    <td className="syllabus-description-cell">
                      <div className="syllabus-description-edit-wrap">
                        <textarea
                          className="syllabus-input syllabus-textarea"
                          placeholder="Enter description"
                          value={editValues.description || ''}
                          onChange={(e) =>
                            setEditValues((v) => ({
                              ...v,
                              description: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                        <div
                          className="syllabus-edit-actions d-flex align-items-center"
                          style={{ gap: '4px' }}
                        >
                          <button
                            className="btn-av-link save-btn"
                            onClick={() => saveEdit(index)}
                            title="Save"
                          >
                            <Icon icon={ICONS.CHECK} className="av-green" />
                          </button>
                          <button
                            className="btn-av-link cancel-btn"
                            onClick={cancelEdit}
                            title="Cancel"
                          >
                            <Icon icon={ICONS.CLOSE} className="av-red" />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="syllabus-readings-schedule-cell">
                      <span>{getReadingsScheduleDate(rows[index])}</span>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="syllabus-date-cell">
                      {formatDate(row.date)}
                    </td>
                    <td className="syllabus-topic-cell">
                      {row.topic || row.name || ''}
                    </td>
                    <td className="syllabus-description-cell">
                      <span
                        className="syllabus-description-preview"
                        title={row.description || ''}
                      >
                        {getDescriptionPreview(row.description || '')}
                        {row.description &&
                          row.description.length >
                            DESCRIPTION_PREVIEW_LIMIT && (
                            <button
                              type="button"
                              className="syllabus-link-button more-link"
                              onClick={() => openDescriptionModal(index)}
                            >
                              More
                            </button>
                          )}
                      </span>
                    </td>
                    <td className="syllabus-readings-schedule-cell">
                      <div className="readings-schedule-cell-content">
                        <span className="readings-schedule-date">
                          {getReadingsScheduleDate(row)}
                        </span>
                        <ToolTip
                          toolTipText="Generate now"
                          toolTipPosition="top"
                        >
                          <button
                            type="button"
                            className="generate-now-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateReadings(row, index);
                            }}
                            disabled={
                              generatingReadings[index] || editingIndex !== null
                            }
                          >
                            {generatingReadings[index]
                              ? 'Generating...'
                              : 'Generate now'}
                          </button>
                        </ToolTip>
                      </div>
                    </td>
                    <td className="syllabus-actions-cell">
                      <div
                        className="d-flex align-items-start"
                        style={{ gap: '8px' }}
                      >
                        <button
                          className="btn-av-link edit-btn"
                          onClick={() => startEdit(index)}
                          title="Edit"
                          disabled={editingIndex !== null}
                        >
                          <Icon icon={ICONS.PENCIL} />
                        </button>
                        <button
                          className="btn-av-link delete-btn"
                          onClick={() => deleteRow(index)}
                          title="Delete"
                          disabled={editingIndex !== null}
                        >
                          <Icon icon={ICONS.DELETE} />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            <tr>
              <td
                colSpan="5"
                onClick={addRow}
                className={editingIndex !== null ? 'disabled-add-row' : ''}
                style={{
                  cursor: editingIndex !== null ? 'not-allowed' : 'pointer',
                  opacity: editingIndex !== null ? 0.5 : 1,
                }}
              >
                + Add week
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
